import * as React from 'react'
import produce from 'immer'
import { useImmer } from 'use-immer'
import { get, set } from 'lodash'
import {
  IonItem,
  IonLabel,
  IonText,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonSpinner,
} from '@ionic/react'
import { getMultiSelectDisplayNameFromArray, getMultiSelectDisplayNameFromObject } from 'helpers/formViews'
import { t } from 'helpers/i18n'
import { showError } from 'helpers/errors'
import assetStatusOptions from 'options/assets/statusOptions'
import ModalSelect from 'elements/ModalSelect'

export default function (props) {
  const { filterDto = {}, onChange = () => {}, onClose = () => {} } = props

  const [state, updateState] = useImmer({})

  const setState = React.useCallback((name, value) => {
    updateState((draft) => {
      set(draft, name, value)
    })
  }, [])

  const setFilter = (path, value) => {
    onChange(
      produce(filterDto, (draft) => {
        set(draft, path, value)
      })
    )
  }

  const assignFilter = (value) => {
    onChange(
      produce(filterDto, (draft) => {
        Object.assign(draft, value)
      })
    )
  }

  async function fetchItems() {
    try {
      setState('loadingIsOpen', true)

      const responses = await Promise.all([props.getLocations(), props.getAssetCategories()])

      updateState((draft) => {
        draft.loadingIsOpen = false
        draft.locations = get(responses[0], 'value.data.items', [])
        draft.assetCategories = get(responses[1], 'value.data.items', [])
      })
    } catch (error) {
      setState('loadingIsOpen', false)
      showError({ error })
    }
  }

  React.useEffect(() => {
    fetchItems()
  }, [])

  const locationIds = filterDto?.locationIds ?? []
  const assetStatusList = filterDto?.assetStatusList ?? []
  const assetCategoryIds = filterDto?.assetCategoryIds ?? []

  if (state.activeView === 'locationIds') {
    return (
      <ModalSelect
        title={t('location')}
        options={(state.locations ?? []).map((each) => ({
          value: each.id,
          text: each.displayName,
        }))}
        value={locationIds}
        onChange={(values) => setFilter('locationIds', values)}
        onCancel={() => setState('activeView', null)}
        allowMultiple
      />
    )
  }

  if (state.activeView === 'assetCategoryIds') {
    return (
      <ModalSelect
        title={t('assetCategory')}
        options={(state.assetCategories ?? []).map((each) => ({
          value: each.id,
          text: each.displayName,
        }))}
        value={assetCategoryIds}
        onChange={(values) => {
          assignFilter({
            assetCategoryIds: values,
            assetIds: [],
          })
        }}
        onCancel={() => setState('activeView', null)}
        allowMultiple
      />
    )
  }

  if (state.activeView === 'assetStatusList') {
    return (
      <ModalSelect
        title={t('status')}
        options={Object.entries(assetStatusOptions).map((each) => ({
          value: each[0],
          text: t(each[1]),
        }))}
        value={assetStatusList}
        onChange={(values) => setFilter('assetStatusList', values)}
        onCancel={() => setState('activeView', null)}
        allowMultiple
      />
    )
  }

  if (state.loadingIsOpen) {
    return <IonSpinner className="ion-margin" color="light" />
  }

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton color="secondary" onClick={() => onChange({})}>
              {t('clear')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('filter')}</IonTitle>
          <IonButtons slot="end">
            <IonButton color="secondary" onClick={onClose}>
              {t('close')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent forceOverscroll={false}>
        <IonItem
          lines="full"
          className="tofino-stacked-item"
          onClick={() => setState('activeView', 'locationIds')}
          button
        >
          <IonLabel>
            <IonText color="medium">
              <small>{t('location')}</small>
            </IonText>
            <br />
            {getMultiSelectDisplayNameFromArray(state.locations, locationIds)}
          </IonLabel>
        </IonItem>
        <IonItem
          lines="full"
          className="tofino-stacked-item"
          onClick={() => setState('activeView', 'assetCategoryIds')}
          button
        >
          <IonLabel>
            <IonText color="medium">
              <small>{t('assetCategory')}</small>
            </IonText>
            <br />
            {getMultiSelectDisplayNameFromArray(state.assetCategories, assetCategoryIds)}
          </IonLabel>
        </IonItem>
        <IonItem
          lines="full"
          className="tofino-stacked-item"
          onClick={() => setState('activeView', 'assetStatusList')}
          button
        >
          <IonLabel>
            <IonText color="medium">
              <small>{t('status')}</small>
            </IonText>
            <br />
            {getMultiSelectDisplayNameFromObject(assetStatusOptions, assetStatusList)}
          </IonLabel>
        </IonItem>
      </IonContent>
    </>
  )
}
